import BreadcrumbsSchema from "modules/magnitude/components/BreadcrumbsSchema";
import SideMenuMobileAdapter from "components/SideMenu/SideMenuMobileAdapter/SideMenuMobileAdapter";
import { stickyBottomBarFirstTriggerStyles } from "components/StickyBottomBar";
import StickyBottomBarTrigger from "components/StickyBottomBar/StickyBottomBarTrigger";
import { DataContextProvider } from "contexts/DataContext";
import { useLanguage, useTranslations } from "contexts/LocalizationContext";
import { MobileMenuContextProvider } from "contexts/MobileMenuContext";
import { ModalContextProvider } from "contexts/ModalContext";
import { SideMenuContextProvider } from "contexts/SideMenuContext";
import { StickyBottomBarContextProvider } from "contexts/StickyBottomBarContext";
import CustomHead from "modules/common/components/CustomHead";
import Footer from "modules/common/components/Footer";
import { CheckCompensationButton, HeaderNav, MenuItems } from "modules/common/components/HeaderNav";
import { PressLogos } from "modules/common/components/PressLogos";
import { LANGUAGES_TO_DISPLAY_LUFTHANSA_TOP_BAR, SLUGS_TO_DISPLAY_LUFTHANSA_TOP_BAR } from "modules/home-page/utils";
import { useRouter } from "next/router";
import { useTopMenu } from "utils/menuUtils";
import { ChakraBox as Box } from "@airhelp/react-ui-components";
import StickyBottomBarAdapter from "../../../storyblok/adapters/StickyBottomBar";
import Page from "../Page/Page";
import Schema from "../Schema/Schema";
import type { MagnitudeTemplateProps } from "./MagnitudeTemplate.d";

const MagnitudeTemplate = ({
  cmsLanguagesData,
  template,
  fullSlug,
  whiteListDomains = [],
  serverSideStoryblokPages,
}: MagnitudeTemplateProps) => {
  const language = useLanguage();
  const translations = useTranslations();
  const topMenu = useTopMenu(language);

  const metaTitle = template.title;
  const metaDesc = template.data?.meta_description.value;
  const metaKeywords = template.data?.meta_keywords.value;
  const schema = template.data?.schema?.value;
  const breadcrumbsPositionTranslationKeyMap = { Airlines: "menu_airlines", Airports: "menu_airports" };
  const breadcrumbsPositionName = breadcrumbsPositionTranslationKeyMap[template.topic];

  const {
    query: { secondUrlSegment },
  } = useRouter();

  let topBannerText;
  let topBannerButtonText;
  if (
    LANGUAGES_TO_DISPLAY_LUFTHANSA_TOP_BAR.includes(language) &&
    // @ts-ignore
    SLUGS_TO_DISPLAY_LUFTHANSA_TOP_BAR.includes(secondUrlSegment)
  ) {
    topBannerText = translations("top_bar_affected_by_lufthansa_strike");
    topBannerButtonText = translations("top_bar_submit_claim_now");
  }
  const marginTop = topBannerText && topBannerButtonText ? { base: 20, md: 10 } : 0;

  return (
    <StickyBottomBarContextProvider threshold={0.4}>
      <DataContextProvider data={{ cmsLanguagesData, serverSideStoryblokPages, whiteListDomains }}>
        <Schema
          language={language}
          title={metaTitle}
          description={metaDesc}
          keywords={metaKeywords}
          canonical={fullSlug}
          cmsPageSchema={schema}
          datePublished={template.datePublished}
          dateModified={template.dateModified}
        />
        <CustomHead title={metaTitle} description={metaDesc} ogTitle={metaTitle} ogDescription={metaDesc} />
        <BreadcrumbsSchema
          currentPositionName={template.iataCode}
          parentPositionName={translations(breadcrumbsPositionName)}
        />
        <MobileMenuContextProvider>
          <ModalContextProvider>
            <SideMenuContextProvider>
              <StickyBottomBarTrigger type="first" styles={stickyBottomBarFirstTriggerStyles} />
              <HeaderNav
                menuItems={<MenuItems menu={topMenu} />}
                headerButtons={<CheckCompensationButton />}
                topBannerText={topBannerText}
                topBannerButtonText={topBannerButtonText}
                subMenu={<SideMenuMobileAdapter pageHeading={template.data.heading.value} />}
              />
              <Box mt={marginTop} />
              <Page
                components={template.components}
                pageData={template.data}
                iataCode={template.iataCode}
                topic={template.topic}
                linkboxPages={template.linkboxPages}
              />
            </SideMenuContextProvider>
            <PressLogos />
            <Footer />
          </ModalContextProvider>
        </MobileMenuContextProvider>
        <StickyBottomBarAdapter />
      </DataContextProvider>
    </StickyBottomBarContextProvider>
  );
};

export default MagnitudeTemplate;
